import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import WarningIcon from "@material-ui/icons/Warning";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Comunicados do sistema (super admin). Obrigatório: não fecha sem aceite e só
// habilita o aceite após o fim do texto. Não obrigatório: fecha a qualquer momento.
// O vídeo (YouTube) é opcional e NÃO bloqueia o aceite.

const YT_API_SRC = "https://www.youtube.com/iframe_api";
let ytApiPromise = null;

const loadYouTubeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (previous) previous();
        resolve(window.YT);
      };
      const script = document.createElement("script");
      script.src = YT_API_SRC;
      script.async = true;
      script.onerror = () => {
        ytApiPromise = null;
        reject(new Error("youtube_api_load_failed"));
      };
      document.body.appendChild(script);
    });
  }
  return ytApiPromise;
};

const waitForElement = (id, tries = 30) =>
  new Promise((resolve, reject) => {
    const attempt = left => {
      const el = document.getElementById(id);
      if (el) return resolve(el);
      if (left <= 0) return reject(new Error("element_not_found"));
      setTimeout(() => attempt(left - 1), 100);
    };
    attempt(tries);
  });

const SystemNoticeGate = () => {
  const { user } = useContext(AuthContext);
  const [queue, setQueue] = useState([]);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [videoDone, setVideoDone] = useState(false);
  const [videoMaxPct, setVideoMaxPct] = useState(0);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  const playerRef = useRef(null);
  const timerRef = useRef(null);
  const mandatoryAcceptedRef = useRef(false);

  const current = queue[0] || null;
  const noticeKey = current ? `${current.notice_id}:${current.version}` : "";
  const elementId = current ? `system-notice-yt-${current.notice_id}-${current.version}` : "";
  const videoId = current?.video_id || null;

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await api.get("/system-notices/pending");
      setQueue(data.items || []);
    } catch (err) {
      // Falha de rede: tenta de novo no próximo evento de pendência.
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return undefined;
    fetchPending();
    window.addEventListener("system-notices:pending", fetchPending);
    return () => window.removeEventListener("system-notices:pending", fetchPending);
  }, [user?.id, fetchPending]);

  const checkEnd = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    // Conteúdo que cabe na tela já está "no fim": sem isso o botão nunca aparece.
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 8) setReachedEnd(true);
  }, []);

  useEffect(() => {
    setReachedEnd(false);
    setVideoDone(false);
    setVideoMaxPct(0);
    setChecked(false);
    const t = setTimeout(checkEnd, 150);
    window.addEventListener("resize", checkEnd);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", checkEnd);
    };
  }, [noticeKey, checkEnd]);

  useEffect(() => {
    if (!videoId) return undefined;
    let cancelled = false;

    loadYouTubeApi()
      .then(YT => waitForElement(elementId).then(el => ({ YT, el })))
      .then(({ YT, el }) => {
        if (cancelled) return;
        playerRef.current = new YT.Player(el, {
          videoId,
          playerVars: { rel: 0, modestbranding: 1 },
          events: {
            onStateChange: e => {
              if (e.data === YT.PlayerState.ENDED) setVideoDone(true);
            }
          }
        });
        timerRef.current = setInterval(() => {
          const p = playerRef.current;
          if (!p || typeof p.getDuration !== "function") return;
          const duration = p.getDuration();
          if (duration > 0) {
            const pct = Math.round((p.getCurrentTime() / duration) * 100);
            setVideoMaxPct(prev => Math.max(prev, pct));
          }
        }, 1000);
      })
      .catch(() => {
        // Vídeo indisponível: o aviso segue normal, sem métricas de vídeo.
      });

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      if (playerRef.current && typeof playerRef.current.destroy === "function") {
        playerRef.current.destroy();
      }
      playerRef.current = null;
    };
  }, [noticeKey, videoId, elementId]);

  const advance = () => {
    setQueue(q => q.slice(1));
  };

  const handleAccept = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await api.post(`/system-notices/${current.notice_id}/accept`, {
        version: current.version,
        videoCompleted: videoDone,
        videoMaxPct: current.video_id ? videoMaxPct : null
      });
      mandatoryAcceptedRef.current = true;
      advance();
    } catch (err) {
      // 409 (versão mudou ou aviso desativado): recarrega a fila sem aceitar.
      await fetchPending();
    } finally {
      setBusy(false);
    }
  };

  // Não obrigatório: só fecha depois de gravar a visualização. Se falhar, permanece aberto.
  const handleClose = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await api.post(`/system-notices/${current.notice_id}/view`, { version: current.version });
      advance();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  // Quando não restam obrigatórios na fila, recarrega: as requisições suspensas pelo
  // bloqueio (428) só são retomadas com o estado atualizado. Avisos não obrigatórios
  // restantes reaparecem após o reload.
  useEffect(() => {
    if (mandatoryAcceptedRef.current && !queue.some(n => n.mandatory)) {
      mandatoryAcceptedRef.current = false;
      window.location.reload();
    }
  }, [queue]);

  if (!current) return null;

  const mandatory = current.mandatory === true;
  const confirmEnabled = checked && (!mandatory || reachedEnd) && !busy;
  const confirm = () => (mandatory ? handleAccept() : handleClose());

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      disableBackdropClick
      disableEscapeKeyDown
      aria-labelledby="system-notice-title"
      PaperProps={{ style: { borderRadius: 12, overflow: "hidden" } }}
    >
      <div style={{ background: "#F59E0B", height: 6 }} />
      <DialogTitle id="system-notice-title" disableTypography style={{ paddingBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <WarningIcon style={{ color: "#D97706", fontSize: 34 }} />
          <span style={{ color: "#B91C1C", fontWeight: 800, fontSize: 22, letterSpacing: 0.3 }}>
            COMUNICADO IMPORTANTE!
          </span>
        </div>
      </DialogTitle>
      <DialogContent ref={bodyRef} onScroll={checkEnd} style={{ maxHeight: "55vh", overflowY: "auto" }}>
        {current.video_id && (
          <div style={{ marginBottom: 16 }}>
            <div id={elementId} style={{ width: "100%", height: 300 }} />
          </div>
        )}
        <div
          style={{
            background: "#FFF7E0",
            border: "1px solid #F5C451",
            borderRadius: 10,
            padding: 16
          }}
        >
          <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8 }}>
            {current.title}
          </Typography>
          <Typography variant="body1" component="div" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
            {current.body}
          </Typography>
        </div>
      </DialogContent>
      <DialogActions style={{ flexDirection: "column", alignItems: "stretch", gap: 8, padding: "12px 24px 20px" }}>
        {mandatory && !reachedEnd && (
          <Typography variant="caption" color="textSecondary">
            Role até o fim do texto para habilitar a confirmação.
          </Typography>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={checked}
                disabled={busy || (mandatory && !reachedEnd)}
                onChange={e => setChecked(e.target.checked)}
              />
            }
            label={<span style={{ fontSize: 14 }}>Declaro que li e compreendi este comunicado</span>}
          />
          <Button
            variant="contained"
            disabled={!confirmEnabled}
            onClick={confirm}
            style={{
              background: confirmEnabled ? "#0B2545" : undefined,
              color: confirmEnabled ? "#FFFFFF" : undefined,
              fontWeight: 700,
              borderRadius: 24,
              padding: "8px 22px"
            }}
          >
            Entendi e Fechar
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default SystemNoticeGate;
